/**
 * HappyFox chat loader (nulo-oxygen domUtils.js parity).
 */

const HAPPY_FOX_EVENTS_BOUND_KEY = '__NULO_HAPPYFOX_EVENTS_BOUND__';
const HAPPY_FOX_EXPANDED_KEY = '__NULO_HAPPYFOX_EXPANDED__';
const HAPPY_FOX_LAST_BOTTOM_KEY = '__NULO_HAPPYFOX_LAST_BOTTOM__';
const HAPPY_FOX_Z_INDEX_APPLIED_KEY = '__NULO_HAPPYFOX_Z_INDEX_APPLIED__';
const HAPPY_FOX_STYLE_RAF_KEY = '__NULO_HAPPYFOX_STYLE_RAF__';
const HAPPY_FOX_CLEANUP_KEY = '__NULO_HAPPYFOX_CLEANUP__';
const STICKY_CTA_STATE_KEY = '__NULO_STICKY_CTA__';
const STICKY_CTA_EVENT_NAME = 'nulo:sticky-cta-state';
const STICKY_CTA_SPACING = 35;
const HAPPY_FOX_EXPANDED_Z_INDEX = '100';
const HAPPY_FOX_COLLAPSED_Z_INDEX = '20';
/** How far (px) the icon sits above bottom-0 when the footer is near (mobile only) */
const FOOTER_LIFT_PX = 65;
/** Window flag — set true when footer enters the proximity threshold */
const FOOTER_NEAR_KEY = '__NULO_HFC_FOOTER_NEAR__';
/** Custom event dispatched when footer proximity changes */
const FOOTER_NEAR_EVENT = 'nulo:hfc-footer-near';

/**
 * @param {string} src
 * @param {string} scriptElemId
 */
function loadScript(src, scriptElemId) {
  if (!scriptElemId || !src) return;
  if (document.getElementById(scriptElemId)) return;
  if (document.querySelector(`script[src="${src}"]`)) return;

  const firstScript = document.getElementsByTagName('script')[0];
  const scriptTag = document.createElement('script');
  scriptTag.type = 'text/javascript';
  scriptTag.src = src;
  scriptTag.id = scriptElemId;
  scriptTag.defer = true;
  firstScript.parentNode?.insertBefore(scriptTag, firstScript);
}

const getHappyFoxFrame = () => document.querySelector('#hfc-frame');

const getStickyCtaState = () => {
  const state = window[STICKY_CTA_STATE_KEY];
  return {
    visible: Boolean(state?.visible),
    height: Number.isFinite(state?.height) ? state.height : 0,
  };
};

const getHappyFoxCollapsedBottom = () => {
  const stickyState = getStickyCtaState();
  // Bottom driven by sticky CTA bar (PDP buy bar / PLP filter bar)
  const fromStickyBar = stickyState.visible
    ? stickyState.height + STICKY_CTA_SPACING
    : 0;
  // Bottom driven by footer proximity — mobile only (< 768px)
  const isMobile = window.innerWidth < 768;
  const fromFooter = isMobile && window[FOOTER_NEAR_KEY] ? FOOTER_LIFT_PX : 0;
  // Use the larger of the two so both sources layer correctly
  return `${Math.max(fromStickyBar, fromFooter)}px`;
};

/**
 * @param {{ expanded: boolean }} options
 */
function applyHappyFoxFrameBottom({ expanded }) {
  const frame = getHappyFoxFrame();
  if (!frame) return;

  const bottom = expanded ? '0px' : getHappyFoxCollapsedBottom();
  if (window[HAPPY_FOX_LAST_BOTTOM_KEY] !== bottom) {
    frame.style.bottom = bottom;
    window[HAPPY_FOX_LAST_BOTTOM_KEY] = bottom;
  }

  if (expanded) {
    if (!window[HAPPY_FOX_Z_INDEX_APPLIED_KEY]) {
      frame.style.setProperty('z-index', HAPPY_FOX_EXPANDED_Z_INDEX, 'important');
      window[HAPPY_FOX_Z_INDEX_APPLIED_KEY] = true;
    }
  } else if (window[HAPPY_FOX_Z_INDEX_APPLIED_KEY]) {
    frame.style.removeProperty('z-index');
    frame.style.setProperty('z-index', HAPPY_FOX_COLLAPSED_Z_INDEX, 'important');
    window[HAPPY_FOX_Z_INDEX_APPLIED_KEY] = false;
  }
}

function bindHappyFoxEvents() {
  if (typeof window[HAPPY_FOX_CLEANUP_KEY] === 'function') {
    window[HAPPY_FOX_CLEANUP_KEY]();
  }
  if (window[HAPPY_FOX_EVENTS_BOUND_KEY]) return;

  const chat = window.HappyFoxChat;
  if (!chat || typeof chat.on !== 'function') return;

  const onExpand = () => {
    window[HAPPY_FOX_EXPANDED_KEY] = true;
    applyHappyFoxFrameBottom({ expanded: true });
  };

  const onCollapse = () => {
    window[HAPPY_FOX_EXPANDED_KEY] = false;
    applyHappyFoxFrameBottom({ expanded: false });
  };

  chat.on('expand:chatbox', onExpand);
  chat.on('collapse:chatbox', onCollapse);

  const updateFrame = () => {
    if (window[HAPPY_FOX_STYLE_RAF_KEY]) {
      cancelAnimationFrame(window[HAPPY_FOX_STYLE_RAF_KEY]);
    }
    window[HAPPY_FOX_STYLE_RAF_KEY] = requestAnimationFrame(() => {
      applyHappyFoxFrameBottom({
        expanded: Boolean(window[HAPPY_FOX_EXPANDED_KEY]),
      });
      window[HAPPY_FOX_STYLE_RAF_KEY] = 0;
    });
  };

  const cleanup = () => {
    if (window[HAPPY_FOX_STYLE_RAF_KEY]) {
      cancelAnimationFrame(window[HAPPY_FOX_STYLE_RAF_KEY]);
      window[HAPPY_FOX_STYLE_RAF_KEY] = 0;
    }
    window.removeEventListener('resize', updateFrame);
    window.removeEventListener('orientationchange', updateFrame);
    window.removeEventListener(STICKY_CTA_EVENT_NAME, updateFrame);
    window.removeEventListener(FOOTER_NEAR_EVENT, updateFrame);
    window.removeEventListener('pagehide', cleanup);
    window[HAPPY_FOX_EVENTS_BOUND_KEY] = false;
    window[HAPPY_FOX_CLEANUP_KEY] = null;
  };

  window.addEventListener('resize', updateFrame);
  window.addEventListener('orientationchange', updateFrame);
  window.addEventListener(STICKY_CTA_EVENT_NAME, updateFrame);
  // Re-compute bottom when footer proximity changes
  window.addEventListener(FOOTER_NEAR_EVENT, updateFrame);
  window.addEventListener('pagehide', cleanup);

  window[HAPPY_FOX_EVENTS_BOUND_KEY] = true;
  window[HAPPY_FOX_CLEANUP_KEY] = cleanup;
  updateFrame();
}

function initHappyFox() {
  const config = window.NuloTheme?.happyfox;
  if (!config?.embedToken) return;

  if (window.HappyFoxChat) {
    bindHappyFoxEvents();
  }

  // Start footer proximity observer immediately — matches nulo-oxygen startFooterObserver()
  // Observes <nulo-footer> element; lifts HFC widget 80px on mobile when footer is near.
  startFooterObserver();

  window.HFCHAT_CONFIG = {
    EMBED_TOKEN: config.embedToken,
    ASSETS_URL: config.assetsUrl || 'https://widget.happyfoxchat.com/visitor',
    onload() {
      window.HappyFoxChat = this;
      bindHappyFoxEvents();
    },
  };

  const scrollHandler = () => {
    const scrollY = window.scrollY || window.pageYOffset;
    const pageHeight = document.documentElement.scrollHeight;
    const windowHeight = window.innerHeight;

    if (scrollY <= 0.2 * (pageHeight - windowHeight)) return;

    loadScript(`${window.HFCHAT_CONFIG.ASSETS_URL}/js/widget-loader.js`, 'nulo-happy-fox');

    let framePollAttempts = 0;
    const waitForFrame = () => {
      if (window[HAPPY_FOX_EVENTS_BOUND_KEY]) return;
      const frame = getHappyFoxFrame();
      if (frame) {
        frame.style.setProperty('z-index', HAPPY_FOX_COLLAPSED_Z_INDEX, 'important');
        frame.classList.add('hfc-badge__bottom-left');
        applyHappyFoxFrameBottom({
          expanded: Boolean(window[HAPPY_FOX_EXPANDED_KEY]),
        });
      } else if (framePollAttempts < 20) {
        framePollAttempts += 1;
        setTimeout(waitForFrame, 300);
      }
    };
    waitForFrame();
    window.removeEventListener('scroll', scrollHandler);
  };

  window.addEventListener('scroll', scrollHandler, { passive: true });
  scrollHandler();
}

/**
 * Observe the nulo-footer element and update FOOTER_NEAR_KEY + dispatch
 * FOOTER_NEAR_EVENT when footer enters/leaves the 120px proximity threshold.
 * Matches nulo-oxygen domUtils.js startFooterObserver().
 */
function startFooterObserver() {
  if (typeof IntersectionObserver === 'undefined') return;

  const footer =
    document.querySelector('nulo-footer') ||
    document.querySelector('.footer__wrapper') ||
    document.querySelector('footer');
  if (!footer) return;

  const observer = new IntersectionObserver(
    ([entry]) => {
      const isNear = entry.isIntersecting;
      if (window[FOOTER_NEAR_KEY] !== isNear) {
        window[FOOTER_NEAR_KEY] = isNear;
        window.dispatchEvent(new CustomEvent(FOOTER_NEAR_EVENT));
      }
    },
    {
      // Positive bottom rootMargin expands the viewport downward so the
      // observer fires when the footer top is within 120px of the fold.
      rootMargin: '0px 0px 120px 0px',
      threshold: 0,
    },
  );

  observer.observe(footer);
}

/**
 * Lower HappyFox z-index when overlays are open (nulo-oxygen Layout.jsx).
 * @param {boolean} behindOverlay
 */
function setHappyFoxBehindOverlay(behindOverlay) {
  const frame = getHappyFoxFrame();
  if (!frame) return;
  frame.style.setProperty(
    'z-index',
    behindOverlay ? '14' : HAPPY_FOX_COLLAPSED_Z_INDEX,
    'important'
  );
}

window.NuloHappyFox = { setBehindOverlay: setHappyFoxBehindOverlay };

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHappyFox);
} else {
  initHappyFox();
}
